import { computed, ref, unref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { cleanupSmokeTestProject, fetchSmokeFixtureFile } from '@/api/smokeTestApi';
import { useAuthStore } from '@/stores/useAuthStore';
import { formatSmokeDetail } from '@/utils/smokeTestRedact';
import {
  assignFilesToInput,
  clickByText,
  clickSelector,
  confirmMessageBox,
  errorMessage,
  findByText,
  highlightAndClick,
  queryVisible,
  setNativeInputValue,
  sleep,
  unrefMaybe,
  waitRouteName,
  waitUntil,
} from '@/utils/smokeUiDrive';

export const SMOKE_TEST_USERNAME = 'test';
export const SMOKE_PROJECT_NAME_PREFIX = '[冒烟测试]';

export type SmokeTestStatus = 'idle' | 'running' | 'paused' | 'completed' | 'failed';

export interface SmokeSuspectBug {
  step: string;
  message: string;
  detail?: string;
}

type AiPanelSmokeApi = {
  expandRightPanel?: () => void;
  beginSmokeTestSession?: () => Promise<void> | void;
  appendSmokeTestProgress?: (content: string) => void;
  submitExpandedInput?: (text?: string) => Promise<void> | void;
  isThinking?: unknown;
  aiLoading?: unknown;
  isStreaming?: unknown;
  getScopeActiveQueueTasks?: () => unknown[];
};

export function useSmokeTestRunner(getAiPanel: () => AiPanelSmokeApi | null | undefined) {
  const authStore = useAuthStore();
  const router = useRouter();
  const route = useRoute();

  const status = ref<SmokeTestStatus>('idle');
  const currentStepLabel = ref('');
  const suspectBugs = ref<SmokeSuspectBug[]>([]);
  const opLogs = ref<string[]>([]);
  /** 按钮旁进度气泡（运行/暂停中展示；完成后可短暂保留） */
  const bubbleVisible = ref(false);
  /** 完成后放大的报告弹框 */
  const reportVisible = ref(false);

  let pauseResolve: (() => void) | null = null;
  let abortRequested = false;
  let clickGuardAttached = false;
  let ignoreClickUntil = 0;
  /** 自动化自身点击期间不触发暂停 */
  let driving = false;

  const isSmokeTestUser = computed(
    () => (authStore.userInfo?.username || '').trim().toLowerCase() === SMOKE_TEST_USERNAME,
  );

  const buttonLabel = computed(() => {
    switch (status.value) {
      case 'running':
        return '冒烟测试中…';
      case 'paused':
        return '继续测试';
      case 'completed':
        return '再次冒烟';
      case 'failed':
        return '重试冒烟';
      default:
        return '冒烟测试';
    }
  });

  function log(line: string) {
    const text = line.trim();
    if (!text) return;
    opLogs.value.push(text);
    if (!bubbleVisible.value && (status.value === 'running' || status.value === 'paused')) {
      bubbleVisible.value = true;
    }
  }

  function recordBug(step: string, err: unknown) {
    const message = errorMessage(err);
    const detail = formatSmokeDetail(
      err && typeof err === 'object' && ('data' in err || 'response' in err) ? err : { message },
    );
    suspectBugs.value.push({
      step,
      message,
      detail: typeof detail === 'string' ? detail : JSON.stringify(detail),
    });
    log(`⚠️ **疑似问题**（${step}）：${message}`);
  }

  async function waitIfPaused() {
    while (status.value === 'paused') {
      await new Promise<void>((resolve) => {
        pauseResolve = resolve;
      });
      pauseResolve = null;
    }
    if (abortRequested) throw new Error('冒烟测试已中止');
  }

  function pauseFromUserClick() {
    if (status.value !== 'running' || driving) return;
    status.value = 'paused';
    ElMessage.warning('冒烟测试已暂停：可自行操作；再次点击「继续测试」恢复');
    log('⏸️ **已暂停**（检测到人工点击）。再次点击右上角「继续测试」可恢复。');
  }

  function resume() {
    if (status.value !== 'paused') return;
    status.value = 'running';
    log('▶️ **已继续**冒烟测试');
    pauseResolve?.();
  }

  function onDocumentClickCapture(ev: MouseEvent) {
    if (status.value !== 'running' || driving) return;
    if (Date.now() < ignoreClickUntil) return;
    const t = ev.target as HTMLElement | null;
    if (t?.closest?.('[data-smoke-test-btn="1"], [data-smoke-driving="1"], .smoke-progress-bubble, .smoke-report-dialog')) {
      return;
    }
    if (t?.closest?.('.ai-assistant-root, .el-message-box, .el-overlay, .el-message')) return;
    pauseFromUserClick();
  }

  function attachClickGuard() {
    if (clickGuardAttached) return;
    document.addEventListener('click', onDocumentClickCapture, true);
    clickGuardAttached = true;
  }

  function detachClickGuard() {
    if (!clickGuardAttached) return;
    document.removeEventListener('click', onDocumentClickCapture, true);
    clickGuardAttached = false;
  }

  async function drive<T>(fn: () => Promise<T>): Promise<T> {
    driving = true;
    try {
      return await fn();
    } finally {
      driving = false;
    }
  }

  function isAiBusy(): boolean {
    const panel = getAiPanel();
    if (!panel) return false;
    const thinking = !!unrefMaybe(panel.isThinking as boolean | { value: boolean });
    const loading = !!unrefMaybe(panel.aiLoading as boolean | { value: boolean });
    const streaming = !!unrefMaybe(panel.isStreaming as boolean | { value: boolean });
    const queue = panel.getScopeActiveQueueTasks?.() ?? [];
    const qLen = Array.isArray(queue) ? queue.length : unref(queue as never) ? 1 : 0;
    return thinking || loading || streaming || qLen > 0;
  }

  async function waitAiIdle(opts?: { timeoutMs?: number; label?: string; minBusyMs?: number }) {
    const label = opts?.label || 'AI 执行完成';
    const timeoutMs = opts?.timeoutMs ?? 600_000;
    // 先等到进入 busy（避免任务尚未启动就判 idle）
    const minBusyMs = opts?.minBusyMs ?? 600;
    const start = Date.now();
    let sawBusy = false;
    while (Date.now() - start < 15_000) {
      await waitIfPaused();
      if (isAiBusy()) {
        sawBusy = true;
        break;
      }
      await sleep(200);
    }
    if (!sawBusy) {
      // 某些操作几乎瞬时失败；再稳一下
      await sleep(minBusyMs);
    }
    await waitUntil(
      async () => {
        await waitIfPaused();
        if (isAiBusy()) return false;
        await sleep(700);
        return !isAiBusy();
      },
      { timeoutMs, intervalMs: 500, label },
    );
    log(`⏳ ${label} — AI 已空闲`);
  }

  async function runStep(label: string, fn: () => Promise<void>) {
    await waitIfPaused();
    currentStepLabel.value = label;
    log(`### ▶ ${label}`);
    try {
      await drive(fn);
      log(`✅ ${label} — 完成`);
    } catch (e) {
      recordBug(label, e);
      log(`❌ ${label} — 失败，继续后续步骤`);
    }
  }

  async function navigateSidebar(title: string, expectedRoute?: string) {
    log(`导航侧栏「${title}」…`);
    await clickSelector(`button.rail-item[title="${title}"]`, {
      label: `侧栏 ${title}`,
      settleMs: 500,
    });
    if (expectedRoute) {
      await waitRouteName(() => route.name, expectedRoute, { timeoutMs: 30_000 });
      log(`已进入路由 \`${expectedRoute}\``);
    }
    await sleep(500);
  }

  async function ensurePersonalKnowledgeScope() {
    for (let i = 0; i < 4; i++) {
      const personal = findByText('.ks-breadcrumb-item', '个人');
      if (personal) {
        log('知识空间已在「个人」范围');
        return;
      }
      const sw = queryVisible('.ks-scope-trigger');
      if (!sw) break;
      await highlightAndClick(sw, { settleMs: 400 });
      await sleep(300);
    }
    if (!findByText('.ks-breadcrumb-item', '个人')) {
      throw new Error('未能切换到个人知识空间');
    }
  }

  async function stepKnowledge() {
    await navigateSidebar('知识空间', 'KnowledgeSpace');
    await ensurePersonalKnowledgeScope();

    const stamp = Date.now().toString(36);
    const folderName = `${SMOKE_PROJECT_NAME_PREFIX}KB-${stamp}`;

    log(`点击「新建目录」创建 \`${folderName}\``);
    await clickByText('button.ks-text-btn', '新建目录', { label: '新建目录' });
    await waitUntil(() => !!document.querySelector('.el-dialog'), { label: '新建目录对话框', timeoutMs: 10_000 });
    const nameInput = queryVisible('.el-dialog .el-input__inner') as HTMLInputElement | null;
    if (!nameInput) throw new Error('未找到目录名称输入框');
    await setNativeInputValue(nameInput, folderName);
    await clickByText('.el-dialog__footer button', '确认', { label: '确认新建目录' });
    await sleep(800);

    // 点进新建的目录卡片
    const folderCard =
      findByText('.ks-folder-title, .ks-folder-card, h3', folderName)
      || findByText('.ks-folder-card', folderName);
    if (folderCard) {
      await highlightAndClick(folderCard, { settleMs: 600 });
    } else {
      log('未直接命中目录卡片，尝试在当前视图上传（可能已进入目录）');
    }

    log('上传知识库文件 sample-kb.txt…');
    const file = await fetchSmokeFixtureFile('sample-kb.txt', 'text/plain');
    const uploadBtn = findByText('button.ks-upload-btn', '上传文件');
    if (!uploadBtn) throw new Error('未找到「上传文件」按钮');
    // 先高亮按钮，再写 hidden input（避免系统文件框）
    await highlightAndClick(uploadBtn, { settleMs: 200 });
    const fileInput = queryVisible('input.ks-hidden-input[type="file"]:not([webkitdirectory])') as HTMLInputElement | null;
    if (!fileInput) throw new Error('未找到知识库文件 input');
    await assignFilesToInput(fileInput, [file]);
    log('等待文档卡片出现…');
    await waitUntil(
      () => !!findByText('.ks-doc-title, .ks-doc-card', /sample-kb|冒烟|SMOKE/i) || document.querySelectorAll('.ks-doc-card').length > 1,
      { timeoutMs: 120_000, label: '知识库文档上传完成' },
    );
    await sleep(600);

    log('删除刚上传的文档…');
    const moreBtn =
      queryVisible('.ks-doc-card:not(.ks-doc-card--onboard) .ks-doc-footer button[aria-label="更多"]')
      || queryVisible('.ks-doc-card:not(.ks-doc-card--onboard) .el-dropdown button');
    if (!moreBtn) throw new Error('未找到文档「更多」菜单');
    await highlightAndClick(moreBtn, { settleMs: 300 });
    await sleep(200);
    const delItem = findByText('.el-dropdown-menu__item', '删除');
    if (!delItem) throw new Error('未找到「删除」菜单项');
    await highlightAndClick(delItem, { settleMs: 300 });
    await confirmMessageBox(/删除|确定/);
    await sleep(600);

    log('删除知识库目录…');
    // 回到个人根或当前条带找目录删
    const folderMore =
      findByText('.ks-folder-card', folderName)?.querySelector('button, .el-dropdown')
      || queryVisible('.ks-folder-card .el-dropdown button');
    if (folderMore) {
      await highlightAndClick(folderMore as HTMLElement, { settleMs: 300 });
      const fd = findByText('.el-dropdown-menu__item', '删除');
      if (fd) {
        await highlightAndClick(fd, { settleMs: 300 });
        await confirmMessageBox(/删除/);
        await sleep(500);
      }
    } else {
      log('未找到目录删除入口（可能已随文档清理），记为软性跳过');
    }
  }

  async function stepCreateProjectViaAi(ctx: { projectId?: number; projectName?: string }) {
    await navigateSidebar('项目管理', 'Dashboard');
    const panel = getAiPanel();
    panel?.expandRightPanel?.();
    await sleep(400);

    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const projectName = `${SMOKE_PROJECT_NAME_PREFIX}${stamp}`;
    ctx.projectName = projectName;

    const prompt =
      `请帮我创建一个新项目。项目名称必须为「${projectName}」，客户名称「冒烟测试客户」，` +
      `项目目标是验证 AIPD 主流程冒烟测试（知识库、成员、SOR、部件推荐、图纸评审、BOM）。` +
      `请给出推荐的阶段计划和风险提示，并自动填充创建项目表单。`;

    log('通过 AI 对话发起创建项目（将等待 AI 流式返回与跳转）…');
    log(`> ${prompt}`);
    if (!panel?.submitExpandedInput) {
      throw new Error('AI 面板未暴露 submitExpandedInput');
    }
    await panel.submitExpandedInput(prompt);
    await waitAiIdle({ label: '创建项目 AI 对话', timeoutMs: 600_000, minBusyMs: 1500 });

    // 等待跳到创建页或已建好项目详情
    try {
      await waitUntil(
        () => route.name === 'ProjectCreate' || route.name === 'ProjectDashboard',
        { timeoutMs: 180_000, label: '进入创建项目页或项目详情' },
      );
    } catch {
      log('AI 未自动跳转，尝试打开 `/projects/create` 并沿用对话结果…');
      await router.push({ name: 'ProjectCreate' });
      await waitRouteName(() => route.name, 'ProjectCreate', { timeoutMs: 20_000 });
    }

    if (route.name === 'ProjectCreate') {
      log('已进入创建项目页，校验/填写项目名称并点击「生成项目」…');
      const nameInput =
        (queryVisible('.project-create-page .el-form-item:nth-child(1) .el-input__inner') as HTMLInputElement | null)
        || (findByText('.el-form-item', '项目名称')?.querySelector('.el-input__inner') as HTMLInputElement | null)
        || (queryVisible('.el-form-item .el-input__inner') as HTMLInputElement | null);
      if (nameInput) {
        const cur = (nameInput.value || '').trim();
        if (!cur.includes(SMOKE_PROJECT_NAME_PREFIX)) {
          await setNativeInputValue(nameInput, projectName);
        } else {
          ctx.projectName = cur;
        }
      }
      await sleep(400);
      await clickByText('button', '生成项目', { label: '生成项目', settleMs: 500 });
      // 可能仍有校验 loading
      await waitUntil(
        () => route.name === 'ProjectDashboard',
        { timeoutMs: 180_000, label: '生成项目后进入项目详情' },
      );
    }

    await waitRouteName(() => route.name, 'ProjectDashboard', { timeoutMs: 30_000 });
    const idRaw = route.params.id ?? route.params.projectId;
    const id = Number(Array.isArray(idRaw) ? idRaw[0] : idRaw);
    if (!Number.isFinite(id) || id <= 0) throw new Error('未能解析项目 id');
    ctx.projectId = id;
    log(`项目已创建：id=\`${id}\` route=\`${String(route.fullPath)}\``);
  }

  async function stepMembers(ctx: { removedUserId?: number }) {
    if (route.name !== 'ProjectDashboard') {
      throw new Error('当前不在项目详情页');
    }
    log('切换到「项目信息」Tab…');
    await clickByText('.el-tabs__item', '项目信息', { label: '项目信息 Tab', settleMs: 500 });

    log('点击「添加成员」…');
    await clickSelector('button.member-chip-add-btn[aria-label="添加成员"], button.member-chip-add-btn', {
      label: '添加成员',
      settleMs: 500,
    });
    await waitUntil(
      () => !!document.querySelector('.member-org-picker-dialog, .el-dialog'),
      { label: '成员选择对话框', timeoutMs: 15_000 },
    );
    await sleep(500);

    // 尝试搜索预置账号对应展示名（系统管理员/项目经理…）
    const searchInput = queryVisible(
      '.el-dialog .el-input__inner[placeholder*="搜索"], .member-org-picker-dialog .el-input__inner',
    ) as HTMLInputElement | null;
    if (searchInput) {
      await setNativeInputValue(searchInput, '系统管理员');
      await sleep(600);
    }

    let rows = Array.from(
      document.querySelectorAll('.el-dialog .el-table__body-wrapper tbody tr, .member-org-picker-dialog tbody tr'),
    ) as HTMLElement[];
    if (!rows.length && searchInput) {
      await setNativeInputValue(searchInput, '项目经理');
      await sleep(600);
      rows = Array.from(
        document.querySelectorAll('.el-dialog .el-table__body-wrapper tbody tr'),
      ) as HTMLElement[];
    }
    if (!rows.length && searchInput) {
      await setNativeInputValue(searchInput, '');
      await sleep(600);
      rows = Array.from(
        document.querySelectorAll('.el-dialog .el-table__body-wrapper tbody tr'),
      ) as HTMLElement[];
    }
    if (!rows.length) throw new Error('成员列表为空（无候选人可点选；请确认已预置 admin/pm 等账号用户）');
    const myName = (authStore.userInfo?.realName || '').trim();
    const target =
      rows.find((r) => {
        const t = (r.innerText || '').trim();
        return t && (!myName || !t.includes(myName));
      }) || rows[0];
    log(`勾选成员行：${(target.innerText || '').split('\n')[0]}`);
    await highlightAndClick(target, { settleMs: 350 });
    await clickByText('.el-dialog__footer button, .member-org-picker-dialog button', '确定', {
      label: '确认添加成员',
      settleMs: 600,
    });
    await sleep(800);

    const chips = Array.from(document.querySelectorAll('.member-chip, .member-chip-remove')).length;
    log(`添加后成员芯片相关节点数≈${chips}`);

    log('删除刚添加的成员…');
    const removeBtns = Array.from(
      document.querySelectorAll('button.member-chip-remove[aria-label="移除成员"], button.member-chip-remove'),
    ) as HTMLElement[];
    if (!removeBtns.length) throw new Error('未找到「移除成员」按钮');
    // 优先点非负责人的最后一个
    const btn = removeBtns[removeBtns.length - 1];
    await highlightAndClick(btn, { settleMs: 400 });
    // 可能弹出二次确认
    if (document.querySelector('.el-message-box')) {
      await confirmMessageBox(/确认|确定|移除/);
    }
    await sleep(800);
    log('已尝试移除成员');
  }

  async function stepSorAnalyze() {
    log('切换「需求与工程设计」→「需求清单」…');
    await clickByText('.el-tabs__item', '需求与工程设计', { label: '需求与工程设计 Tab', settleMs: 600 });
    await clickByText('button.engineering-sidebar__item', '需求清单', { label: '需求清单', settleMs: 500 });

    log('上传 SOR（sample-sor.pdf）…');
    const addCard = findByText('.req-attach-card--add', '上传文件') || queryVisible('.req-attach-card--add');
    if (!addCard) throw new Error('未找到 SOR「上传文件」入口');
    await highlightAndClick(addCard, { settleMs: 200 });
    const sorInput = queryVisible(
      '.req-attach-zone input.hidden-file-input[type="file"], input.hidden-file-input[accept*="pdf"]',
    ) as HTMLInputElement | null;
    if (!sorInput) throw new Error('未找到 SOR file input');
    const sorFile = await fetchSmokeFixtureFile('sample-sor.pdf', 'application/pdf');
    await assignFilesToInput(sorInput, [sorFile]);
    await waitUntil(
      () => !!findByText('.req-attach-card__name, .req-attach-card', /sample-sor|SOR|冒烟/i),
      { timeoutMs: 120_000, label: 'SOR 附件出现在列表' },
    );
    const sorCard =
      findByText('.req-attach-card:not(.req-attach-card--add)', /sample-sor/i)
      || queryVisible('.req-attach-card:not(.req-attach-card--add)');
    if (sorCard) await highlightAndClick(sorCard, { settleMs: 300 });
    log('SOR 已上传，点击「生成需求」并等待 AI…');
    const genBtn =
      queryVisible('button[data-smoke="generate-requirements"]')
      || findByText('button.embedded-link--primary, button.embedded-link', '生成需求');
    if (!genBtn || genBtn.hasAttribute('disabled')) {
      throw new Error('「生成需求」不可点（请确认已上传并选中 SOR）');
    }
    await highlightAndClick(genBtn, { settleMs: 500 });
    // 可能有重新生成确认
    if (document.querySelector('.el-message-box')) {
      await confirmMessageBox(/确定|确认|生成|继续/);
    }
    // 知识库选择弹框：可不选知识库，直接确认生成
    const kbConfirm =
      findByText('.req-gen-kb-dialog button, .el-dialog__footer button', '确认生成')
      || findByText('button.el-button--primary', '确认生成');
    if (kbConfirm) {
      await highlightAndClick(kbConfirm, { settleMs: 400 });
    }
    await waitAiIdle({ label: 'SOR 需求分析', timeoutMs: 600_000, minBusyMs: 2000 });
    await sleep(1000);
  }

  async function stepComponentRecommend() {
    log('切换「通用部件推荐」并点击「一键推荐」…');
    await clickByText('button.engineering-sidebar__item', '通用部件推荐', {
      label: '通用部件推荐',
      settleMs: 500,
    });
    await clickSelector('button[data-smoke="component-recommend"], button.bom-link-action', {
      label: '一键推荐',
      settleMs: 500,
    }).catch(async () => {
      await clickByText('button.bom-link-action, button', '一键推荐', { label: '一键推荐', settleMs: 500 });
    });
    await waitAiIdle({ label: '部件推荐', timeoutMs: 600_000, minBusyMs: 1500 });
    await waitUntil(
      () => !findByText('button.bom-link-action, button', '推荐中…'),
      { timeoutMs: 30_000, label: '按钮离开「推荐中…」' },
    );
  }

  async function stepDrawingReviewAndBom() {
    log('切换「BOM单」…');
    await clickByText('button.engineering-sidebar__item', 'BOM单', { label: 'BOM单', settleMs: 500 });

    log('上传图纸 sample-drawing.pdf…');
    const uploadCard =
      queryVisible('.bom-cad-attach-card--add')
      || findByText('.bom-cad-attach-card', '上传文件');
    if (!uploadCard) throw new Error('未找到 BOM「上传文件」卡片');
    await highlightAndClick(uploadCard, { settleMs: 200 });
    const cadInput = queryVisible(
      '.bom-cad-uploader input[type="file"], .bom-cad-panel input[type="file"]',
    ) as HTMLInputElement | null;
    if (!cadInput) throw new Error('未找到图纸 file input');
    const drawing = await fetchSmokeFixtureFile('sample-drawing.pdf', 'application/pdf');
    await assignFilesToInput(cadInput, [drawing]);

    log('等待图纸卡片就绪（解析完成或可勾选）…');
    await waitUntil(
      () => {
        const card = findByText('.bom-cad-attach-card', /sample-drawing|SMOKE|pdf/i);
        if (!card) return false;
        const parsing = card.classList.contains('is-parsing') || /解析中/.test(card.innerText || '');
        if (parsing) return false;
        return true;
      },
      { timeoutMs: 600_000, label: '图纸 2D 解析完成', intervalMs: 1000 },
    );

    // 勾选图纸
    const check =
      queryVisible('.bom-cad-attach-card:not(.bom-cad-attach-card--add) .bom-cad-attach-card__check:not(.is-disabled)')
      || queryVisible('.bom-cad-attach-card.is-done .bom-cad-attach-card__check');
    if (check) {
      await highlightAndClick(check, { settleMs: 300 });
    } else {
      const card = queryVisible('.bom-cad-attach-card:not(.bom-cad-attach-card--add)');
      if (card) await highlightAndClick(card, { settleMs: 300 });
    }

    log('点击「图纸审核」并等待 AI…');
    const reviewBtn =
      queryVisible('button[data-smoke="drawing-review"]')
      || findByText('button.bom-link-action, button', '图纸审核');
    if (!reviewBtn || reviewBtn.hasAttribute('disabled')) {
      throw new Error('「图纸审核」不可点（请确认图纸已解析并勾选）');
    }
    await highlightAndClick(reviewBtn, { settleMs: 500 });
    await waitAiIdle({ label: '图纸评审', timeoutMs: 600_000, minBusyMs: 1500 });
    await waitUntil(
      () => !findByText('button.bom-link-action, button', '审核中…'),
      { timeoutMs: 60_000, label: '离开「审核中…」' },
    );

    log('点击「生成BOM」并等待 AI…');
    const bomBtn =
      queryVisible('button[data-smoke="bom-generate"]')
      || findByText('button.bom-link-action, button', '生成BOM');
    if (!bomBtn || bomBtn.hasAttribute('disabled')) {
      throw new Error('「生成BOM」不可点');
    }
    await highlightAndClick(bomBtn, { settleMs: 500 });
    await waitAiIdle({ label: '生成 BOM', timeoutMs: 600_000, minBusyMs: 1500 });
    await waitUntil(
      () => !findByText('button.bom-link-action, button', '生成中…'),
      { timeoutMs: 60_000, label: '离开「生成中…」' },
    );
  }

  async function stepCleanup(ctx: { projectId?: number }) {
    if (!ctx.projectId) {
      log('无项目 id，跳过项目清理');
      return;
    }
    log(`调用后台清理接口 DELETE /api/smoke-test/projects/${ctx.projectId} …`);
    const cleaned = await cleanupSmokeTestProject(ctx.projectId);
    log(`清理结果：\n\`\`\`json\n${formatSmokeDetail(cleaned)}\n\`\`\``);
  }

  async function executeSuite() {
    const ctx: { projectId?: number; projectName?: string } = {};

    await runStep('上传 / 删除知识库（页面操作）', stepKnowledge);
    await runStep('AI 对话创建项目（等待 AI）', () => stepCreateProjectViaAi(ctx));
    await runStep('添加 / 删除项目成员（页面操作）', stepMembers);
    await runStep('上传 SOR 并分析需求（等待 AI）', stepSorAnalyze);
    await runStep('自动推荐部件（等待 AI）', stepComponentRecommend);
    await runStep('图纸评审 + 生成 BOM（等待解析与 AI）', stepDrawingReviewAndBom);
    await runStep('清理测试数据', () => stepCleanup(ctx));

    const bugs = suspectBugs.value;
    if (!bugs.length) {
      log('---\n## 🎉 冒烟测试结束\n\n未记录疑似 Bug（各步骤均走完 UI + AI 等待）。');
    } else {
      const lines = bugs.map(
        (b, i) =>
          `${i + 1}. **${b.step}**：${b.message}${
            b.detail
              ? `\n   <details><summary>详情</summary>\n\n\`\`\`\n${b.detail}\n\`\`\`\n</details>`
              : ''
          }`,
      );
      log(`---\n## 冒烟测试结束 — 疑似 Bug 总结（${bugs.length}）\n\n${lines.join('\n\n')}`);
    }
  }

  async function start() {
    if (!isSmokeTestUser.value) {
      ElMessage.error('仅冒烟测试账号可用');
      return;
    }
    if (status.value === 'running') {
      // 运行中若收起了气泡，再次点击按钮只展开进度，不重跑
      bubbleVisible.value = true;
      return;
    }
    if (status.value === 'paused') {
      bubbleVisible.value = true;
      resume();
      return;
    }

    try {
      await ElMessageBox.confirm(
        '将以真实页面跳转与按钮点击执行冒烟测试（可见高亮动画）。AI 创建项目 / SOR 分析 / 图纸评审 / BOM 等会占用右侧 AI 对话框，进度请看「冒烟测试」按钮旁的气泡。请尽量不要手动点击页面（点击会暂停；暂停后点「继续测试」可恢复）。完成后气泡会放大为报告弹框。',
        '启动冒烟测试',
        { confirmButtonText: '开始', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    abortRequested = false;
    suspectBugs.value = [];
    opLogs.value = [];
    currentStepLabel.value = '';
    reportVisible.value = false;
    bubbleVisible.value = true;
    status.value = 'running';
    ignoreClickUntil = Date.now() + 1000;
    attachClickGuard();
    log('🧪 冒烟测试已开始。进度仅显示在本气泡中，不会占用 AI 对话框。');

    try {
      await executeSuite();
      status.value = 'completed';
      bubbleVisible.value = false;
      reportVisible.value = true;
      ElMessage.success('冒烟测试已完成');
    } catch (e) {
      status.value = 'failed';
      recordBug('整体流程', e);
      log(`## 冒烟测试异常中止\n\n${errorMessage(e)}`);
      bubbleVisible.value = false;
      reportVisible.value = true;
      ElMessage.error('冒烟测试中止，请查看报告弹框');
    } finally {
      detachClickGuard();
      currentStepLabel.value = '';
      if (status.value === 'running') {
        status.value = 'completed';
        bubbleVisible.value = false;
        reportVisible.value = true;
      }
    }
  }

  function onButtonClick() {
    ignoreClickUntil = Date.now() + 800;
    void start();
  }

  function collapseBubble() {
    bubbleVisible.value = false;
  }

  function openReport() {
    reportVisible.value = true;
  }

  function setReportVisible(v: boolean) {
    reportVisible.value = v;
  }

  return {
    isSmokeTestUser,
    status,
    buttonLabel,
    currentStepLabel,
    suspectBugs,
    opLogs,
    bubbleVisible,
    reportVisible,
    onButtonClick,
    collapseBubble,
    openReport,
    setReportVisible,
  };
}
